import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "reactstrap";
import Select from "react-select";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { SwitchToggle } from "../../../Components/TableHelper";
import { pagewiseFaqSchema } from "../../../Validation/Validation";
import axiosInstance from "../../../axiosInterceptor";
import Breadcrumb from "../../../Components/Breadcrumb";
import CodeEditor from "../../../Components/CodeEditor";
import { customStyles } from "../../../utils/constant";

const AddPageWiseFaq = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state;
  const editingId = editData?._id;

  const [loading, setLoading] = useState(false);
  const [menuData, setMenuData] = useState([]);
  const [subMenuData, setSubMenuData] = useState([]);

  const initialValues = {
    sortOrder: editData?.sortOrder || 1,
    // If FAQ was saved with only a sub menu, get the parent menu from the sub menu
    menuId:
      editData?.menuId?._id ||
      editData?.menuId ||
      editData?.subMenuId?.menuId?._id ||
      editData?.subMenuId?.menuId ||
      null,
    subMenuId: editData?.subMenuId?._id || editData?.subMenuId || null,
    question: {
      englishLanguage: editData?.question?.englishLanguage || "",
      hindiLanguage: editData?.question?.hindiLanguage || "",
    },
    answer: {
      englishLanguage: editData?.answer?.englishLanguage || "",
      hindiLanguage: editData?.answer?.hindiLanguage || "",
    },
    isActive: editData?.isActive ?? true,
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    setFieldError,
  } = useFormik({
    initialValues,
    validationSchema: pagewiseFaqSchema,
    onSubmit: async (formValues) => {
      // If the selected menu has sub menus, one must be chosen
      if (subMenuData.length > 0 && !formValues.subMenuId) {
        setFieldError("subMenuId", "Sub menu is required");
        setFieldTouched("subMenuId", true, false);
        return;
      }

      const payload = { ...formValues };

      if (payload.subMenuId) {
        // Sub menu selected -> send ONLY subMenuId
        delete payload.menuId;
      } else {
        // No sub menu -> send ONLY menuId
        delete payload.subMenuId;
      }

      setLoading(true);
      try {
        const response = editingId
          ? await axiosInstance.put(`/pagewisefaq/updatefaq/${editingId}`, payload)
          : await axiosInstance.post("/pagewisefaq/createfaq", payload);

        if (response.status === 200) {
          toast.success(response.data.message);
          navigate("/pageWise-faq");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  // Load all menus once
  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        const res = await axiosInstance.get("menu/getallmenu");
        setMenuData(res.data?.data || []);
      } catch (err) {
        setMenuData([]);
      }
    };
    fetchMenuData();
  }, []);

  // Load sub menus whenever the selected menu changes
  useEffect(() => {
    if (!values.menuId) {
      setSubMenuData([]);
      return;
    }

    const fetchSubMenuData = async () => {
      try {
        // TODO: replace with your real endpoint
        const res = await axiosInstance.get(
          `submenu/getsubmenubymenu/${values.menuId}`,
        );
        setSubMenuData(res.data?.data || []);
      } catch (err) {
        setSubMenuData([]);
      }
    };
    fetchSubMenuData();
  }, [values.menuId]);

  const err = (path) => {
    const [a, b] = path.split(".");
    return b ? touched[a]?.[b] && errors[a]?.[b] : touched[a] && errors[a];
  };

  const menuOptions = menuData.map((item) => ({
    label: item.menuName,
    value: item._id,
  }));

  const subMenuOptions = subMenuData.map((item) => ({
    label: item.subMenuName,
    value: item._id,
  }));

  const breadcrumbdata = [
    { label: "Home", path: "/dashboard" },
    { label: "Pagewise FAQ's", path: "/pageWise-faq" },
    { label: editingId ? "Update" : "Add", path: "*" },
  ];

  return (
    <div className="content-wrapper">
      <div className="page-header mb-3">
        <h3 className="custom-page-title">
          {editingId ? "Update" : "Add"} Pagewise FAQ
        </h3>
        <Breadcrumb items={breadcrumbdata} />
      </div>

      <div className="col-md-12 grid-margin stretch-card p-0">
        <div className="card m-0">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Menu */}
                <div
                  className={
                    subMenuOptions.length > 0
                      ? "col-sm-6 form-group"
                      : "col-sm-12 form-group"
                  }
                >
                  <label htmlFor="menuId" className="col-form-label">
                    Select Menu
                  </label>
                  <Select
                    inputId="menuId"
                    placeholder="Select Menu"
                    isClearable
                    styles={customStyles}
                    options={menuOptions}
                    value={
                      menuOptions.find((opt) => opt.value === values.menuId) ||
                      null
                    }
                    onChange={(option) => {
                      setFieldValue("menuId", option ? option.value : null);
                      setFieldValue("subMenuId", null); // reset child
                    }}
                    onBlur={() => setFieldTouched("menuId", true)}
                    name="menuId"
                  />
                  {touched.menuId && errors.menuId && (
                    <small className="text-danger">{errors.menuId}</small>
                  )}
                </div>

                {/* Sub menu (only when the menu has sub menus) */}
                {subMenuOptions.length > 0 && (
                  <div className="col-sm-6 form-group">
                    <label htmlFor="subMenuId" className="col-form-label">
                      Select Sub Menu
                    </label>
                    <Select
                      inputId="subMenuId"
                      placeholder="Select Sub Menu"
                      isClearable
                      styles={customStyles}
                      options={subMenuOptions}
                      value={
                        subMenuOptions.find(
                          (opt) => opt.value === values.subMenuId,
                        ) || null
                      }
                      onChange={(option) =>
                        setFieldValue("subMenuId", option ? option.value : null)
                      }
                      onBlur={() => setFieldTouched("subMenuId", true)}
                      name="subMenuId"
                    />
                    {touched.subMenuId && errors.subMenuId && (
                      <small className="text-danger">{errors.subMenuId}</small>
                    )}
                  </div>
                )}

                {/* Sort order */}
                <div className="col-sm-12 form-group">
                  <label htmlFor="sortOrder" className="col-form-label">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    min={1}
                    id="sortOrder"
                    name="sortOrder"
                    placeholder="Enter sort order"
                    className="border border-dark form-control"
                    value={values.sortOrder}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {err("sortOrder") && (
                    <small className="text-danger">{errors.sortOrder}</small>
                  )}
                </div>

                {/* Questions */}
                <div className="col-md-6 form-group">
                  <label className="col-form-label">Question (English)</label>
                  <textarea
                    className="form-control border border-dark"
                    name="question.englishLanguage"
                    placeholder="Enter question in English"
                    rows={5}
                    value={values.question.englishLanguage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {err("question.englishLanguage") && (
                    <small className="text-danger">
                      {errors.question.englishLanguage}
                    </small>
                  )}
                </div>

                <div className="col-md-6 form-group">
                  <label className="col-form-label">Question (Hindi)</label>
                  <textarea
                    className="form-control border border-dark"
                    name="question.hindiLanguage"
                    placeholder="Enter question in Hindi"
                    rows={5}
                    value={values.question.hindiLanguage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {err("question.hindiLanguage") && (
                    <small className="text-danger">
                      {errors.question.hindiLanguage}
                    </small>
                  )}
                </div>

                {/* Answers */}
                <div className="col-sm-12 form-group">
                  <label className="col-form-label">Answer (English)</label>
                  <CodeEditor
                    value={values.answer.englishLanguage}
                    onChange={(val) =>
                      setFieldValue("answer.englishLanguage", val)
                    }
                  />
                  {err("answer.englishLanguage") && (
                    <small className="text-danger">
                      {errors.answer.englishLanguage}
                    </small>
                  )}
                </div>

                <div className="col-sm-12 form-group">
                  <label className="col-form-label">Answer (Hindi)</label>
                  <CodeEditor
                    value={values.answer.hindiLanguage}
                    onChange={(val) =>
                      setFieldValue("answer.hindiLanguage", val)
                    }
                  />
                  {err("answer.hindiLanguage") && (
                    <small className="text-danger">
                      {errors.answer.hindiLanguage}
                    </small>
                  )}
                </div>

                <div className="col-md-12 mt-4">
                  <SwitchToggle
                    checked={values.isActive}
                    onChange={(val) => setFieldValue("isActive", val)}
                  />
                </div>
              </div>

              <div className="text-center mt-4">
                <Button type="submit" color="primary" disabled={loading}>
                  {loading ? (
                    <span className="d-inline-flex align-items-center gap-2">
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      Processing...
                    </span>
                  ) : (
                    "Save"
                  )}
                </Button>

                <Link to="/pageWise-faq">
                  <Button color="danger" className="mx-2">
                    Cancel
                  </Button>
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPageWiseFaq;

// import { useEffect, useState } from "react";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { Button } from "reactstrap";
// import Select from "react-select";
// import { useFormik } from "formik";
// import { toast } from "react-toastify";
// import { SwitchToggle } from "../../../Components/TableHelper";
// import { faqSchema, pagewiseFaqSchema } from "../../../Validation/Validation";
// import axiosInstance from "../../../axiosInterceptor";
// import Breadcrumb from "../../../Components/Breadcrumb";
// import CodeEditor from "../../../Components/CodeEditor";
// import { customStyles } from "../../../utils/constant";

// const AddPageWiseFaq = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const editData = location.state;
//   const editingId = editData?._id;

//   const [loading, setLoading] = useState(false);
//   const [menuData, setMenuData] = useState([]);
//   const [subMenuData, setSubMenuData] = useState([]);

//   const initialValues = {
//     sortOrder: editData?.sortOrder || 1,
//     // works for populated object ({ _id }) and plain id string
//     menuId: editData?.menuId?._id || editData?.menuId || null,
//     subMenuId: editData?.subMenuId?._id || editData?.subMenuId || null,
//     question: {
//       englishLanguage: editData?.question?.englishLanguage || "",
//       hindiLanguage: editData?.question?.hindiLanguage || "",
//     },
//     answer: {
//       englishLanguage: editData?.answer?.englishLanguage || "",
//       hindiLanguage: editData?.answer?.hindiLanguage || "",
//     },
//     isActive: editData?.isActive ?? true,
//   };

//   const {
//     values,
//     errors,
//     touched,
//     handleChange,
//     handleBlur,
//     handleSubmit,
//     setFieldValue,
//     setFieldTouched,
//     setFieldError,
//   } = useFormik({
//     initialValues,
//     validationSchema: pagewiseFaqSchema,
//     onSubmit: async (formValues) => {
//       // if the selected menu has sub menus, one must be chosen
//       if (subMenuData.length > 0 && !formValues.subMenuId) {
//         setFieldError("subMenuId", "Sub menu is required");
//         setFieldTouched("subMenuId", true, false);
//         return;
//       }

//       const payload = { ...formValues };
//       // Joi rejects null / "", so don't send subMenuId when empty
//       if (!payload.subMenuId) delete payload.subMenuId;

//       setLoading(true);
//       try {
//         const response = editingId
//           ? await axiosInstance.put(`/pagewisefaq/updatefaq/${editingId}`, payload)
//           : await axiosInstance.post("/pagewisefaq/createfaq", payload);

//         if (response.status === 200) {
//           toast.success(response.data.message);
//           navigate("/pageWise-faq");
//         }
//       } catch (error) {
//         toast.error(error.response?.data?.message || "Something went wrong");
//       } finally {
//         setLoading(false);
//       }
//     },
//   });

//   // Load all menus once
//   useEffect(() => {
//     const fetchMenuData = async () => {
//       try {
//         const res = await axiosInstance.get("menu/getallmenu");
//         setMenuData(res.data?.data || []);
//       } catch (err) {
//         setMenuData([]);
//       }
//     };
//     fetchMenuData();
//   }, []);

//   // Load sub menus whenever the selected menu changes
//   useEffect(() => {
//     if (!values.menuId) {
//       setSubMenuData([]);
//       return;
//     }

//     const fetchSubMenuData = async () => {
//       try {
//         // TODO: replace with your real endpoint
//         const res = await axiosInstance.get(
//           `submenu/getsubmenubymenu/${values.menuId}`,
//         );
//         setSubMenuData(res.data?.data || []);
//       } catch (err) {
//         setSubMenuData([]);
//       }
//     };
//     fetchSubMenuData();
//   }, [values.menuId]);

//   /*
//     If your menu API already returns sub menus inside each menu
//     (e.g. item.subMenus), delete the effect above and use this instead:

//     useEffect(() => {
//       const selected = menuData.find((m) => m._id === values.menuId);
//       setSubMenuData(selected?.subMenus || []);
//     }, [values.menuId, menuData]);
//   */

//   const err = (path) => {
//     const [a, b] = path.split(".");
//     return b ? touched[a]?.[b] && errors[a]?.[b] : touched[a] && errors[a];
//   };

//   const menuOptions = menuData.map((item) => ({
//     label: item.menuName,
//     value: item._id,
//   }));

//   const subMenuOptions = subMenuData.map((item) => ({
//     label: item.subMenuName,
//     value: item._id,
//   }));

//   const breadcrumbdata = [
//     { label: "Home", path: "/dashboard" },
//     { label: "Pagewise FAQ's", path: "/pageWise-faq" },
//     { label: editingId ? "Update" : "Add", path: "*" },
//   ];

//   return (
//     <div className="content-wrapper">
//       <div className="page-header mb-3">
//         <h3 className="custom-page-title">
//           {editingId ? "Update" : "Add"} Pagewise FAQ
//         </h3>
//         <Breadcrumb items={breadcrumbdata} />
//       </div>

//       <div className="col-md-12 grid-margin stretch-card p-0">
//         <div className="card m-0">
//           <div className="card-body">
//             <form onSubmit={handleSubmit}>
//               <div className="row">
//                 {/* Menu */}
//                 <div
//                   className={
//                     subMenuOptions.length > 0
//                       ? "col-sm-6 form-group"
//                       : "col-sm-12 form-group"
//                   }
//                 >
//                   <label htmlFor="menuId" className="col-form-label">
//                     Select Menu
//                   </label>
//                   <Select
//                     inputId="menuId"
//                     placeholder="Select Menu"
//                     isClearable
//                     styles={customStyles}
//                     options={menuOptions}
//                     value={
//                       menuOptions.find((opt) => opt.value === values.menuId) ||
//                       null
//                     }
//                     onChange={(option) => {
//                       setFieldValue("menuId", option ? option.value : null);
//                       setFieldValue("subMenuId", null); // reset child
//                     }}
//                     onBlur={() => setFieldTouched("menuId", true)}
//                     name="menuId"
//                   />
//                   {touched.menuId && errors.menuId && (
//                     <small className="text-danger">{errors.menuId}</small>
//                   )}
//                 </div>

//                 {/* Sub menu (only when the menu has sub menus) */}
//                 {subMenuOptions.length > 0 && (
//                   <div className="col-sm-6 form-group">
//                     <label htmlFor="subMenuId" className="col-form-label">
//                       Select Sub Menu
//                     </label>
//                     <Select
//                       inputId="subMenuId"
//                       placeholder="Select Sub Menu"
//                       isClearable
//                       styles={customStyles}
//                       options={subMenuOptions}
//                       value={
//                         subMenuOptions.find(
//                           (opt) => opt.value === values.subMenuId,
//                         ) || null
//                       }
//                       onChange={(option) =>
//                         setFieldValue("subMenuId", option ? option.value : null)
//                       }
//                       onBlur={() => setFieldTouched("subMenuId", true)}
//                       name="subMenuId"
//                     />
//                     {touched.subMenuId && errors.subMenuId && (
//                       <small className="text-danger">{errors.subMenuId}</small>
//                     )}
//                   </div>
//                 )}

//                 {/* Sort order */}
//                 <div className="col-sm-12 form-group">
//                   <label htmlFor="sortOrder" className="col-form-label">
//                     Sort Order
//                   </label>
//                   <input
//                     type="number"
//                     min={1}
//                     id="sortOrder"
//                     name="sortOrder"
//                     placeholder="Enter sort order"
//                     className="border border-dark form-control"
//                     value={values.sortOrder}
//                     onChange={handleChange}
//                     onBlur={handleBlur}
//                   />
//                   {err("sortOrder") && (
//                     <small className="text-danger">{errors.sortOrder}</small>
//                   )}
//                 </div>

//                 {/* Questions */}
//                 <div className="col-md-6 form-group">
//                   <label className="col-form-label">Question (English)</label>
//                   <textarea
//                     className="form-control border border-dark"
//                     name="question.englishLanguage"
//                     placeholder="Enter question in English"
//                     rows={5}
//                     value={values.question.englishLanguage}
//                     onChange={handleChange}
//                     onBlur={handleBlur}
//                   />
//                   {err("question.englishLanguage") && (
//                     <small className="text-danger">
//                       {errors.question.englishLanguage}
//                     </small>
//                   )}
//                 </div>

//                 <div className="col-md-6 form-group">
//                   <label className="col-form-label">Question (Hindi)</label>
//                   <textarea
//                     className="form-control border border-dark"
//                     name="question.hindiLanguage"
//                     placeholder="Enter question in Hindi"
//                     rows={5}
//                     value={values.question.hindiLanguage}
//                     onChange={handleChange}
//                     onBlur={handleBlur}
//                   />
//                   {err("question.hindiLanguage") && (
//                     <small className="text-danger">
//                       {errors.question.hindiLanguage}
//                     </small>
//                   )}
//                 </div>

//                 {/* Answers */}
//                 <div className="col-sm-12 form-group">
//                   <label className="col-form-label">Answer (English)</label>
//                   <CodeEditor
//                     value={values.answer.englishLanguage}
//                     onChange={(val) =>
//                       setFieldValue("answer.englishLanguage", val)
//                     }
//                   />
//                   {err("answer.englishLanguage") && (
//                     <small className="text-danger">
//                       {errors.answer.englishLanguage}
//                     </small>
//                   )}
//                 </div>

//                 <div className="col-sm-12 form-group">
//                   <label className="col-form-label">Answer (Hindi)</label>
//                   <CodeEditor
//                     value={values.answer.hindiLanguage}
//                     onChange={(val) =>
//                       setFieldValue("answer.hindiLanguage", val)
//                     }
//                   />
//                   {err("answer.hindiLanguage") && (
//                     <small className="text-danger">
//                       {errors.answer.hindiLanguage}
//                     </small>
//                   )}
//                 </div>

//                 <div className="col-md-12 mt-4">
//                   <SwitchToggle
//                     checked={values.isActive}
//                     onChange={(val) => setFieldValue("isActive", val)}
//                   />
//                 </div>
//               </div>

//               <div className="text-center mt-4">
//                 <Button type="submit" color="primary" disabled={loading}>
//                   {loading ? (
//                     <span className="d-inline-flex align-items-center gap-2">
//                       <span
//                         className="spinner-border spinner-border-sm"
//                         role="status"
//                         aria-hidden="true"
//                       />
//                       Processing...
//                     </span>
//                   ) : (
//                     "Save"
//                   )}
//                 </Button>

//                 <Link to="/pageWise-faq">
//                   <Button color="danger" className="mx-2">
//                     Cancel
//                   </Button>
//                 </Link>
//               </div>
//             </form>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AddPageWiseFaq;


// // import { useEffect, useState } from "react";
// // import { Link, useLocation, useNavigate } from "react-router-dom";
// // import { Button } from "reactstrap";
// // import { SwitchToggle } from "../../../Components/TableHelper";
// // import { useFormik } from "formik";
// // import { faqSchema } from "../../../Validation/Validation";
// // import axiosInstance from "../../../axiosInterceptor";
// // import { toast } from "react-toastify";
// // import Breadcrumb from "../../../Components/Breadcrumb";
// // import CodeEditor from "../../../Components/CodeEditor";
// // import Select from "react-select";
// // import { customStyles } from "../../../utils/constant";

// // const AddPageWiseFaq = () => {
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const editData = location.state;
// //   const editingId = editData?._id;

// //   const [loading, setLoading] = useState(false);
// //   const [menuData, setMenuData] = useState([]);
// //   const [subMenuData, setSubMenuData] = useState([]);
// //   const initialValues = {
// //     sortOrder: editData?.sortOrder || 1,
// //     // handles both populated object and plain id string
// //     menuId: editData?.menuId?._id || null,
// //     subMenuId: editData?.subMenuId?._id || "",
// //     // menuId: editData?.menuId?._id || editData?.menuId || "",
// //     // subMenuId: editData?.subMenuId?._id || editData?.subMenuId || "",
// //     question: {
// //       englishLanguage: editData?.question?.englishLanguage || "",
// //       hindiLanguage: editData?.question?.hindiLanguage || "",
// //     },
// //     answer: {
// //       englishLanguage: editData?.answer?.englishLanguage || "",
// //       hindiLanguage: editData?.answer?.hindiLanguage || "",
// //     },
// //     isActive: editData?.isActive ?? true,
// //   };


// //   useEffect(() => {
// //     const fetchMenuData = async () => {
// //       try {
// //         const res = await axiosInstance.get("menu/getallmenu");
// //         setMenuData(res.data?.data || []);
// //       } catch (err) {
// //         setMenuData([]);
// //       }
// //     };
// //     fetchMenuData();
// //   }, []);

// //   const {
// //     values, errors, touched,
// //     handleChange, handleBlur, handleSubmit, setFieldValue,
// //   } = useFormik({
// //     initialValues,
// //     // validationSchema: faqSchema,
// //     onSubmit: async (values) => {
// //       setLoading(true);
// //       try {
// //         const response = editingId
// //           ? await axiosInstance.put(`/pagewisefaq/updatefaq/${editingId}`, values)
// //           : await axiosInstance.post("/pagewisefaq/createfaq", values);

// //         if (response.status === 200) {
// //           toast.success(response.data.message);
// //           navigate("/pageWise-faq");
// //         }
// //       } catch (error) {
// //         toast.error(error.response?.data?.message || "Something went wrong");
// //       } finally {
// //         setLoading(false);
// //       }
// //     },
// //   });



// //   const err = (path) => {
// //     const [a, b] = path.split(".");
// //     return b ? touched[a]?.[b] && errors[a]?.[b] : touched[a] && errors[a];
// //   };

// //   const breadcrumbdata = [
// //     { label: "Home", path: "/dashboard" },
// //     { label: "Pagewise FAQ's", path: "/pageWise-faq" },
// //     { label: editingId ? "Update" : "Add", path: "*" },
// //   ];


// //   const menuOptions = menuData.map((item) => ({
// //     label: item.menuName,
// //     value: item._id,
// //   }));

// //   const subMenuOptions =
// //     subMenuData &&
// //     subMenuData?.length > 0 &&
// //     subMenuData?.map((item) => ({
// //       label: item.subMenuName,
// //       value: item._id,
// //     }));

// //   const columnClass =
// //     subMenuOptions && subMenuOptions.length > 0
// //       ? "col-sm-6 col-lg-6"
// //       : "col-sm-6";

// //   return (
// //     <div className="content-wrapper">
// //       <div className="page-header mb-3">
// //         <h3 className="custom-page-title">
// //           {editingId ? "Update" : "Add"} Pagewise FAQ
// //         </h3>
// //         <Breadcrumb items={breadcrumbdata} />
// //       </div>

// //       <div className="col-md-12 grid-margin stretch-card p-0">
// //         <div className="card m-0">
// //           <div className="card-body">
// //             <form onSubmit={handleSubmit}>
// //               <div className="row">
// //                 <div className="col-sm-12 form-group">
// //                   <div className="form-group">
// //                     <label htmlFor="menuId" className="col-form-label ">
// //                       Select Menu
// //                     </label>
// //                     <Select
// //                       placeholder=" Select Menu"
// //                       isClearable
// //                       styles={customStyles}
// //                       options={menuOptions}
// //                       value={
// //                         menuOptions.find(
// //                           (opt) => opt.value === values.menuId,
// //                         ) || null
// //                       }
// //                       onChange={(option) => {
// //                         setFieldValue("menuId", option ? option.value : null);
// //                       }}
// //                       name="menuId"
// //                     />
// //                     {touched.menuId && errors.menuId && (
// //                       <small className="text-danger">{errors.menuId}</small>
// //                     )}
// //                   </div>
// //                 </div>

// //                 {subMenuOptions && subMenuOptions?.length > 0 && (

// //                   <div className="form-group">
// //                     <label htmlFor="subMenuId" className="col-form-label">
// //                       Select Sub Menu
// //                     </label>

// //                     <Select
// //                       placeholder="Select Sub Menu"
// //                       isClearable
// //                       styles={customStyles}
// //                       options={subMenuOptions}
// //                       value={
// //                         subMenuOptions.find(
// //                           (opt) => opt.value === values.subMenuId,
// //                         ) || null
// //                       }
// //                       onChange={(option) => {
// //                         setFieldValue(
// //                           "subMenuId",
// //                           option ? option.value : null,
// //                         );
// //                       }}
// //                       name="subMenuId"
// //                       isDisabled={!values.menuId}
// //                     />

// //                     {touched.subMenuId && errors.subMenuId && (
// //                       <small className="text-danger">
// //                         {errors.subMenuId}
// //                       </small>
// //                     )}
// //                   </div>
// //                 )}

// //                 {/* Sort order */}
// //                 <div className="col-sm-12 form-group">
// //                   <label className="col-form-label">Sort Order</label>
// //                   <input
// //                     type="number"
// //                     min={1}
// //                     name="sortOrder"
// //                     className="border border-dark form-control"
// //                     value={values.sortOrder}
// //                     onChange={handleChange}
// //                     onBlur={handleBlur}
// //                   />
// //                   {err("sortOrder") && <small className="text-danger">{errors.sortOrder}</small>}
// //                 </div>

// //                 {/* Questions */}
// //                 <div className="col-md-6 form-group">
// //                   <label className="col-form-label">Question (English)</label>
// //                   <textarea
// //                     className="form-control border border-dark"
// //                     name="question.englishLanguage"
// //                     rows={5}
// //                     value={values.question.englishLanguage}
// //                     onChange={handleChange}
// //                     onBlur={handleBlur}
// //                   />
// //                   {err("question.englishLanguage") && (
// //                     <small className="text-danger">{errors.question.englishLanguage}</small>
// //                   )}
// //                 </div>

// //                 <div className="col-md-6 form-group">
// //                   <label className="col-form-label">Question (Hindi)</label>
// //                   <textarea
// //                     className="form-control border border-dark"
// //                     name="question.hindiLanguage"
// //                     rows={5}
// //                     value={values.question.hindiLanguage}
// //                     onChange={handleChange}
// //                     onBlur={handleBlur}
// //                   />
// //                   {err("question.hindiLanguage") && (
// //                     <small className="text-danger">{errors.question.hindiLanguage}</small>
// //                   )}
// //                 </div>

// //                 {/* Answers */}
// //                 <div className="col-sm-12 form-group">
// //                   <label className="col-form-label">Answer (English)</label>
// //                   <CodeEditor
// //                     value={values.answer.englishLanguage}
// //                     onChange={(val) => setFieldValue("answer.englishLanguage", val)}
// //                   />
// //                   {err("answer.englishLanguage") && (
// //                     <small className="text-danger">{errors.answer.englishLanguage}</small>
// //                   )}
// //                 </div>

// //                 <div className="col-sm-12 form-group">
// //                   <label className="col-form-label">Answer (Hindi)</label>
// //                   <CodeEditor
// //                     value={values.answer.hindiLanguage}
// //                     onChange={(val) => setFieldValue("answer.hindiLanguage", val)}
// //                   />
// //                   {err("answer.hindiLanguage") && (
// //                     <small className="text-danger">{errors.answer.hindiLanguage}</small>
// //                   )}
// //                 </div>

// //                 <div className="col-md-12 mt-4">
// //                   <SwitchToggle
// //                     checked={values.isActive}
// //                     onChange={(val) => setFieldValue("isActive", val)}
// //                   />
// //                 </div>
// //               </div>

// //               <div className="text-center mt-4">
// //                 <Button type="submit" color="primary" disabled={loading}>
// //                   {loading ? (
// //                     <span className="d-inline-flex align-items-center gap-2">
// //                       <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
// //                       Processing...
// //                     </span>
// //                   ) : (
// //                     "Save"
// //                   )}
// //                 </Button>
// //                 <Link to="/pageWise-faq">
// //                   <Button color="danger" className="mx-2">Cancel</Button>
// //                 </Link>
// //               </div>
// //             </form>
// //           </div>
// //         </div>
// //       </div >
// //     </div >
// //   );
// // };

// // export default AddPageWiseFaq;

// // // import { useState } from "react";
// // // import { Link, useLocation, useNavigate } from "react-router-dom";
// // // import { Button } from "reactstrap";
// // // import { SwitchToggle } from "../../../Components/TableHelper";
// // // import { useFormik } from "formik";
// // // import { faqSchema } from "../../../Validation/Validation";
// // // import axiosInstance from "../../../axiosInterceptor";
// // // import { toast } from "react-toastify";
// // // import Breadcrumb from "../../../Components/Breadcrumb";
// // // import CodeEditor from "../../../Components/CodeEditor";

// // // const AddPageWiseFaq = () => {
// // //   const navigator = useNavigate();
// // //   const location = useLocation();
// // //   const editData = location.state;
// // //   const [loading, setLoading] = useState(false);
// // //   const editingId = editData?._id;

// // //   const initalvalues = {
// // //     question: editData?.question || "",
// // //     answer: editData?.answer || "",
// // //     sortOrder: editData?.sortOrder || 1,
// // //     isActive: editData?.isActive ?? true,
// // //   };

// // //   const {
// // //     values,
// // //     errors,
// // //     handleChange,
// // //     handleSubmit,
// // //     setFieldValue,
// // //     touched,
// // //     handleBlur,
// // //   } = useFormik({
// // //     initialValues: initalvalues,
// // //     validationSchema: faqSchema,
// // //     onSubmit: async (values) => {
// // //       setLoading(true);
// // //       try {
// // //         const response = editingId
// // //           ? await axiosInstance.put(`/pagewisefaq/updateFAQ/${editingId}`, values)
// // //           : await axiosInstance.post("/pagewisefaq/createFAQ", values);

// // //         if (response.status === 200) {
// // //           toast.success(response.data.message);
// // //           navigator(`/pageWise-faq`);
// // //         }
// // //       } catch (error) {
// // //         toast.error(error.response?.data?.message);
// // //         return error;
// // //       } finally {
// // //         setLoading(false);
// // //       }
// // //     },
// // //   });

// // //   const breadcrumbdata = [
// // //     { label: "Home", path: "/dashboard" },
// // //     { label: "Pagewise FAQ's", path: `/pageWise-faq` },
// // //     { label: editingId ? "Update" : "Add", path: "*" },
// // //   ];
// // //   return (
// // //     <>
// // //       <div className="content-wrapper">
// // //         <div className="page-header mb-3">
// // //           <h3 className="custom-page-title">
// // //             {editingId ? "Update" : "Add"} Pagewise FAQ
// // //           </h3>
// // //           <Breadcrumb items={breadcrumbdata} />
// // //         </div>
// // //         <div className="col-md-12 grid-margin stretch-card p-0">
// // //           <div className="card m-0">
// // //             <div className="card-body">
// // //               <form onSubmit={handleSubmit}>
// // //                 <div className="row">
// // //                   <div className="col-sm-12">
// // //                     <div className="form-group">
// // //                       <label
// // //                         htmlFor="client-sortOrder"
// // //                         className="col-form-label "
// // //                       >
// // //                         Sort Order
// // //                       </label>
// // //                       <input
// // //                         type="number"
// // //                         className={`border border-dark form-control  `}
// // //                         id="client-sortOrder"
// // //                         name="sortOrder"
// // //                         placeholder="Enter client Sort Order"
// // //                         min={1}
// // //                         value={values.sortOrder}
// // //                         onChange={handleChange}
// // //                       />
// // //                       {touched.sortOrder && errors.sortOrder && (
// // //                         <small className="text-danger">
// // //                           {errors.sortOrder}
// // //                         </small>
// // //                       )}
// // //                     </div>
// // //                   </div>

// // //                   <div className="col-sm-12">
// // //                     <div className="form-group">
// // //                       <label htmlFor="faq-question" className="col-form-label">
// // //                         Question
// // //                       </label>

// // //                       <textarea
// // //                         className="form-control border border-dark"
// // //                         id="faq-question"
// // //                         name="question"
// // //                         placeholder="Enter your question"
// // //                         rows={10}
// // //                         value={values.question || ""}
// // //                         onChange={handleChange}
// // //                         onBlur={handleBlur}
// // //                       />

// // //                       {touched.question && errors.question && (
// // //                         <small className="text-danger">{errors.question}</small>
// // //                       )}
// // //                     </div>
// // //                   </div>

// // //                   <div className="col-sm-12">
// // //                     <div className="form-group">
// // //                       <label htmlFor="faq-answer" className="col-form-label ">
// // //                         Answer
// // //                       </label>

// // //                       <CodeEditor
// // //                         value={values.answer}
// // //                         onChange={(val) => setFieldValue("answer", val)}
// // //                       />

// // //                       {touched.answer && errors.answer && (
// // //                         <small className="text-danger">{errors.answer}</small>
// // //                       )}
// // //                     </div>
// // //                   </div>

// // //                   <div className="col-md-12 mt-4">
// // //                     <SwitchToggle
// // //                       checked={values.isActive}
// // //                       onChange={(val) => setFieldValue("isActive", val)}
// // //                     />
// // //                   </div>
// // //                 </div>

// // //                 <div className="text-center mt-4">
// // //                   <Button type="submit" color="primary" disabled={loading}>
// // //                     {loading ? (
// // //                       <span className="d-inline-flex align-items-center gap-2">
// // //                         <span
// // //                           className="spinner-border spinner-border-sm"
// // //                           role="status"
// // //                           aria-hidden="true"
// // //                         ></span>
// // //                         Processing...
// // //                       </span>
// // //                     ) : (
// // //                       "Save"
// // //                     )}
// // //                   </Button>

// // //                   <Link to={`/pagewise-faq`}>
// // //                     <Button color="danger mx-2">Cancel </Button>
// // //                   </Link>
// // //                 </div>
// // //               </form>
// // //             </div>
// // //           </div>
// // //         </div>
// // //       </div>
// // //     </>
// // //   );
// // // };

// // // export default AddPageWiseFaq;
